import * as React from "react";
import DebouncedInput from "../helper/DebouncedInput";
import { rankItem } from "@tanstack/match-sorter-utils";
import { format } from "date-fns";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  // getFacetedRowModel,
  // getFacetedUniqueValues,
  // getFacetedMinMaxValues,
  // sortingFns,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import TablePageIndicator from "./TablePageIndicator";
import CustomTablePagination from "./TablePagination";
import ComponentLoader from "../ui/pageLoader/ComponentLoader";

import {
  searchIcon,
  editPencilIcon,
  deleteIconDark,
  history,
  downloadIcon,
  rectangle,
} from "../../assetData/icons";
import { MdFilterAlt, MdOutlineFilterAlt } from "react-icons/md";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

function CouponTable(props) {
  const {
    tableData,
    columns,
    setIsEdit,
    setIsDelete,
    setSelectedItemId,
    renderKey,
    filterField,
    actionConfig = [],
    setGlobalFilters,
    loading,
    actions,
    setIsFilter,
    isFilter,
  } = props;

  const [data, setData] = React.useState([]);
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [isHover, setIsHover] = React.useState("hidden");
  const [id, setId] = React.useState("");
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const hoverEffect = (info) => {
    setIsHover("block");
    setId(info);
  };

  const removeHover = () => {
    setIsHover("hidden");
    setId("");
  };

  const customTable = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
      pagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleEditButton = (segmentId) => {
    setIsEdit(true);
    setSelectedItemId(segmentId);
  };

  const handleDeleteButton = (segmentId) => {
    setSelectedItemId(segmentId);
    setIsDelete(true);
  };

  React.useEffect(() => {
    if (customTable.getState().columnFilters[0]?.id === "fullName") {
      if (customTable.getState().sorting[0]?.id !== "fullName") {
        customTable.setSorting([{ id: "fullName", desc: false }]);
      }
    }
  }, [customTable.getState().columnFilters[0]?.id]);

  React.useEffect(() => {
    if (tableData) setData(tableData);
  }, [tableData]);

  if (!data) return null;

  return (
    <div>
      <div className="flex items-center justify-between">
        <TablePageIndicator
          className={"max-md:hidden"}
          currentNumber={customTable.getFilteredRowModel().rows?.length}
          totalNumber={data?.length}
        />

        {/* Actions */}
        <div className="mb-4 flex items-center justify-between gap-4 max-sm:w-full max-sm:flex-col">
          {filterField && (
            <div className="flex w-full gap-4">
              <div className="flex w-full items-baseline justify-center rounded-md border-[1px] border-[#868686] bg-white px-1 py-1 shadow-lg  sm:hidden">
                <div
                  onClick={() => setIsFilter(true)}
                  className="flex items-center space-x-4 text-[17px] font-bold text-neutral-800 opacity-70"
                >
                  {isFilter ? (
                    <MdFilterAlt className={`text-[17px] `} />
                  ) : (
                    <MdOutlineFilterAlt className={`text-[17px]  `} />
                  )}
                  Filter
                </div>
              </div>
              <div className="flex items-center gap-2 border-b-[1px] border-[#636363] max-sm:w-full sm:w-[200px] ">
                <img src={searchIcon} alt="" />
                <DebouncedInput
                  value={globalFilter ?? ""}
                  onChange={(value) => setGlobalFilters(String(value))}
                  className=" w-[200px] bg-transparent py-1 text-neutral-800 focus:outline-none max-sm:w-full"
                  placeholder="Search in Coupons"
                />
              </div>

              <div className="h-[35px] w-[2px] bg-neutral-200 max-md:hidden" />
            </div>
          )}
          <div className="flex w-full items-center gap-4 ">
            {actionConfig.map((action, idx) => (
              <div
                key={action.id}
                className="flex items-center gap-4 max-sm:w-full"
              >
                {idx !== 0 && (
                  <div className="h-[35px] w-[2px] bg-neutral-200 max-md:hidden" />
                )}
                <button
                  className={`flex w-[200px] items-center justify-center gap-2 rounded border-[1px] border-[#636363] py-1 text-center font-bold text-[#636363] max-sm:w-full sm:px-6 ${
                    action?.disable ? "" : "opacity-[55%]"
                  }`}
                  onClick={action.actionHandler}
                  disabled={!action?.disable}
                >
                  <img src={action.image} alt="i4Invest" />
                  {action.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mb-4 flex w-full items-center gap-2 space-x-2 md:justify-end">
        <div className="flex w-fit items-center gap-2 space-x-2">
          <label className="text-[#868686]">Show Rows: </label>
          <select
            className="text-dropdown-text w-[100px] bg-gray-100 py-[0.4rem] text-[15px] text-[#868686] focus:outline-none"
            onChange={(e) => {
              const value =
                e.target.value === "All" ? data.length : Number(e.target.value);
              setPagination((old) => ({
                ...old,
                pageSize: value,
              }));
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="All">All</option>
          </select>
        </div>
      </div>
      <div className="scrollbar-custom overflow-x-scroll">
        <table className="w-full ">
          <thead className="bg-pri-clr-blue">
            {customTable.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="border-1 w-fit border-black px-1 py-2 text-start text-sm uppercase text-white"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <thead className="h-2">
            <tr></tr>
          </thead>

          {data === null && <h1 className="text-black">No Data</h1>}
          {loading && <ComponentLoader />}

          <tbody className="border-[1px] border-neutral-clr-300 pt-2">
            {customTable.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="w-fit border-[1px] border-neutral-clr-300 px-2 py-1 text-sm text-black"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}

                    {cell.id.split("_")[1] === "id" && (
                      <div className="flex items-center justify-center gap-1  [&>button]:shrink-0">
                        <>
                          <button className={`active:scale-90 `}>
                            <img
                              onMouseOver={() =>
                                hoverEffect(cell.getValue("id"))
                              }
                              onMouseLeave={() =>
                                removeHover(cell.getValue("id"))
                              }
                              src={history}
                              alt="i4invest"
                              className="w-[15px]"
                            />
                            {id === row.original.id && (
                              <>
                                <span
                                  className={`${isHover} absolute z-30 ml-[-180px] w-[250px] rounded-[5px] bg-[#D9D9D9] p-[5px]`}
                                >
                                  <span className="z-30">
                                    <img src={rectangle} alt="" />
                                  </span>
                                  <span className="block text-left">
                                    {"Created At : " +
                                      format(
                                        new Date(row.original.created_at),
                                        "dd MMM yyyy, hh:mm:ss",
                                      )}
                                  </span>
                                  <span className="block text-left">
                                    {`Updated By : ${row.original.updated_by}`}
                                  </span>

                                  <span className="block text-left">
                                    {"Updated On : " +
                                      format(
                                        new Date(row.original.updated_at),
                                        "dd MMM yyyy, hh:mm:ss",
                                      )}
                                  </span>
                                </span>
                              </>
                            )}
                          </button>
                          <button
                            onClick={() =>
                              handleEditButton(cell.getValue("id"))
                            }
                            className="active:scale-90"
                            disabled={
                              actions?.edit_perm &&
                              cell.row.original.status === "Active"
                                ? false
                                : true
                            }
                          >
                            <img
                              src={editPencilIcon}
                              alt="i4invest"
                              className={`${
                                actions?.edit_perm &&
                                cell.row.original.status === "Active"
                                  ? ""
                                  : "opacity-[55%]"
                              } `}
                            />
                            <p></p>
                          </button>

                          <button
                            className="active:scale-90"
                            onClick={() =>
                              handleDeleteButton(cell.getValue("id"))
                            }
                            disabled={
                              actions?.delete_perm &&
                              cell.row.original.status === "Active"
                                ? false
                                : true
                            }
                          >
                            <img
                              src={deleteIconDark}
                              alt="i4invest"
                              className={`${
                                actions?.delete_perm &&
                                cell.row.original.status === "Active"
                                  ? ""
                                  : "opacity-[55%]"
                              } `}
                            />
                          </button>
                        </>
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && (
        <CustomTablePagination table={customTable} renderKey={renderKey} />
      )}
    </div>
  );
}

export default CouponTable;
